import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../core/api/client';
import type { Route, Task, SkipReason } from '../../core/domain/types';
import { isTerminal } from '../../core/domain/stateMachine';
import { directionsUrl } from '../../core/domain/distance';
import { geolocation } from '../../platform/geolocation';
import { submitSkip, onQueueChange } from '../../core/offline/syncManager';
import { peekAll } from '../../core/offline/db';
import { doneLocally } from '../../core/offline/doneLocal';

/** Stops the office already confirmed show as finished even on an old saved route (10 Oct). */
function withLocalDone(routes: Route[]): Route[] {
  return routes.map((r) => ({ ...r, tasks: r.tasks.map((x) => {
    const d = isTerminal(x.status) ? null : doneLocally(x.id);
    return d ? { ...x, status: d.kind } : x;
  }) }));
}
import { stopStep, stopEnd } from '../../core/telemetry/stopLog';
import { routeTracker } from '../../core/tracking/tracker';
import { clearToken } from '../../core/offline/db';
import { t } from '../../platform/lang';
import { reportError } from '../../core/telemetry/report';
import { crumb } from '../../core/telemetry/session';
import { InstallPrompt } from '../install/InstallPrompt';
import { InstallGate, needsInstall, installEverShown } from '../install/InstallGate';
import { FaceReference } from '../identity/FaceReference';
import { IdentityCheck } from '../identity/IdentityCheck';
import { OfficeMessages } from './OfficeMessages';
import { SecurityBriefing, briefingSeen } from '../onboarding/SecurityBriefing';

/**
 * The whole day, on one screen.
 *
 * Written after a day the owner called a disaster, from his description of what
 * a driver actually wants: "I open my app, I see my routes, then the stops with
 * two options, start or skip."
 *
 * What it replaces: a screen that showed ONE route — a driver with two could not
 * see the second until the first was finished, which every two-route driver
 * complained about — and a chain of taps (accept route, open stop, accept stop,
 * start stop, arrive) that each asked a question with only one possible answer.
 *
 * The rules this screen is built on:
 *
 *   * Everything is visible. Both routes, every stop, no hunting.
 *   * Two buttons per stop and no third option. Start, or Skip.
 *   * The next stop is already open. Nobody should have to work out where they
 *     are in a list while holding a tote.
 *   * Finished work collapses to one line with a tick. It is a record, not
 *     something to read again.
 *
 * The standard the owner set, and it is the right one: his grandmother should
 * be able to use it.
 */
export function RouteHome() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<Route[] | null>(null);
  const [stale, setStale] = useState(false);
  const [busyTask, setBusyTask] = useState<string | null>(null);
  const [skipFor, setSkipFor] = useState<string | null>(null);
  // A skip waiting for its face scan (17 Oct). See doSkip.
  const [scanSkip, setScanSkip] = useState<{ task: Task; reason: SkipReason; note: string | null } | null>(null);
  const [skipNote, setSkipNote] = useState('');
  const [skipReason, setSkipReason] = useState<SkipReason | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /*
   * Enrollment. This lived only on the old route screen, which the rebuilt flow
   * replaced — so a driver with no face on file would never have been asked,
   * and one driver worked two days without one. It belongs
   * here, before any route is shown: registering is the first thing that
   * happens, not something that can be walked past.
   */
  const [needsFace, setNeedsFace] = useState(false);
  const [askInstall, setAskInstall] = useState(() => needsInstall());
  // Opens the full picture-by-picture instructions, only if the driver asks.
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  /*
   * COULD NOT ASK, as opposed to ASKED AND THE ANSWER WAS NONE.
   *
   * These were the same screen, and on 14 September that cost the fleet an
   * afternoon: a server change had not shipped, every request was refused
   * before it left the phone, and each driver was told "No route today" —
   * which reads as "you have no work", so several simply stopped. Nobody
   * reported a fault because, to them, nothing was broken.
   *
   * An empty day and an unreachable office are completely different facts and
   * must never again look alike.
   */
  const [unreachable, setUnreachable] = useState(false);
  /*
   * Shown once, to a driver who has never seen it. The old route screen showed
   * this and the rebuild lost it — which meant the one screen that explains,
   * specifically and truthfully, that the phone is bound and the face is
   * compared was no longer reaching anybody new. Dismissing is its only action;
   * it blocks nothing.
   */
  const [showBriefing, setShowBriefing] = useState(() => !briefingSeen());

  /*
   * Paint from the phone's own copy before asking the office anything.
   *
   * This runs once, on open, and costs nothing: the data is already on the
   * device because the driver was looking at it a moment ago. The real request
   * below then corrects it. Without this a driver on one bar watched
   * "Loading..." for up to twelve seconds with the answer already in hand.
   */
  useEffect(() => {
    void (async () => {
      try {
        const cached = await api.cachedRoutesToday();
        if (cached) setRoutes((prev) => prev ?? withLocalDone(cached));
      } catch { /* no copy yet; the request below is the only source */ }
    })();
  }, []);

  const load = useCallback(async () => {
    crumb('route:load');
    try {
      const { routes: fresh, stale: isStale } = await api.getTodayRoutes();
      setRoutes(withLocalDone(fresh));
      setStale(Boolean(isStale));
      setUnreachable(false);
    } catch (err) {
      reportError('request_timeout', 'route', err);
      // We did not learn that there is no work. We learned nothing at all.
      setUnreachable(true);
      setRoutes((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /*
   * STOPS STILL SENDING (5 Oct). A stop finished on the phone but not yet
   * received by the office used to show here as OPEN, exactly like one never
   * touched -- a driver saw it "come back" at the next hospital and tried it
   * again from 17 km away. Now it says it is saved and sending, and has no
   * buttons; when the queue empties the list reloads and it moves to done.
   */
  const [sendingIds, setSendingIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    const refresh = async () => {
      try { setSendingIds(new Set((await peekAll()).map((q) => q.taskId))); }
      catch { /* storage unreadable: show the list as the office has it */ }
    };
    void refresh();
    let before = -1;
    return onQueueChange((n) => {
      void refresh();
      if (before !== -1 && n < before) void load();
      before = n;
    });
  }, [load]);

  // Asked once when the app opens. A failure here never blocks the day: if we
  // cannot reach the server, the driver still gets their route.
  useEffect(() => {
    void (async () => {
      try {
        const r = await api.faceReferenceNeeded();
        setNeedsFace(Boolean(r.needed));
      } catch { /* offline: ask again next time */ }
    })();
  }, []);

  /*
   * THE LOCATION TRAIL. Start it whenever there is unfinished work today.
   *
   * This was lost in the rebuild. The old route screen started the tracker when
   * the driver pressed "Start route"; that button no longer exists, and nothing
   * took over the job — so `routeTracker.start()` was called from nowhere at
   * all. The app kept `stop()` on sign-out and dropped the start.
   *
   * The result: NOT ONE position was recorded for any driver after 13 Sep. Not
   * a permissions problem and not the phones — the app simply never asked. The
   * movement trail is the record that tells a driver circling a hospital with a
   * bad pin apart from a driver who was never there, so its absence is the
   * quietest and worst kind of failure.
   *
   * The server decides what is kept: record_positions() stores nothing unless
   * the route is genuinely started, so running the watch here cannot fabricate
   * a trail outside a shift.
   */
  useEffect(() => {
    if (!routes) return;
    const working = routes.some((r) => r.tasks.some((x) => !isTerminal(x.status)));
    if (working) routeTracker.start();
    else void routeTracker.stop();
  }, [routes]);

  /*
   * Keep the GPS chip warm while there is work left.
   *
   * A cold receiver needs 30-60 seconds to get a usable fix; a warm one answers
   * in two or three. The old route screen warmed it and the rebuild dropped
   * that, which is why completions have been carrying fixes 63, 85 and even 191
   * seconds old — every one of those is a step nearer a refusal for a driver
   * standing in exactly the right place.
   */
  useEffect(() => {
    if (!routes) return;
    const working = routes.some((r) => r.tasks.some((x) => !isTerminal(x.status)));
    if (!working) return;
    return geolocation.warmUp();
  }, [routes]);

  /*
   * Keep the screen on while there is work left.
   *
   * A phone that locks every thirty seconds is a phone the driver has to keep
   * waking one-handed while holding a tote, and each wake risks the app being
   * evicted from memory. Released the moment the day is finished, so it never
   * holds the screen on out of a shift.
   *
   * Best effort: not every browser has this, and a driver who denies it still
   * works normally. It is a convenience, never a requirement.
   */
  useEffect(() => {
    if (!routes) return;
    const working = routes.some((r) => r.tasks.some((x) => !isTerminal(x.status)));
    if (!working) return;

    type Sentinel = { release: () => Promise<void> };
    const nav = navigator as Navigator & {
      wakeLock?: { request: (t: 'screen') => Promise<Sentinel> };
    };
    if (!nav.wakeLock) return;

    let held: Sentinel | null = null;
    let dropped = false;

    const take = () => {
      if (dropped || document.visibilityState !== 'visible') return;
      nav.wakeLock?.request('screen')
        .then((s) => { if (dropped) void s.release(); else held = s; })
        .catch(() => { /* denied or unsupported: nothing is lost */ });
    };
    take();

    // The lock is dropped whenever the app is backgrounded -- a call, Maps --
    // and is not restored on its own, so ask again on return.
    const onShow = () => { if (document.visibilityState === 'visible') take(); };
    document.addEventListener('visibilitychange', onShow);

    return () => {
      dropped = true;
      document.removeEventListener('visibilitychange', onShow);
      void held?.release().catch(() => {});
    };
  }, [routes]);

  // Coming back from a stop, or from the background, should show the truth.
  useEffect(() => {
    const onShow = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', onShow);
    return () => document.removeEventListener('visibilitychange', onShow);
  }, [load]);

  /*
   * OFFICE CHANGES WITHOUT REOPENING (11 Oct). The screen is kept on during a
   * route, so a driver can sit on this list for an hour and never trigger the
   * reload above. When the office hands them a route, moves a stop, or removes
   * one for today, they had to close and reopen the app to see it. Now the list
   * re-asks every 3 minutes while it is on screen. This screen only: a driver
   * inside a stop is never interrupted.
   */
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine !== false) void load();
    }, 3 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [load]);

  /**
   * One tap: take the route, take the stop, mark arrival, open the capture.
   *
   * The location is sent so the server can say "you are 600 m away" BEFORE the
   * driver photographs anything. One filled in a whole delivery at Kaiser
   * Fremont and was refused at the end, eleven times.
   */
  const start = async (task: Task) => {
    setBusyTask(task.id);
    setNotice(null);
    try {
      // Best effort and short: a stop must open even where GPS is poor.
      // Advisory only — it drives the "you are far away" warning — so a couple
      // of minutes old is acceptable and waiting for better is not.
      let fix = geolocation.getLastKnownFix(120_000);
      try { fix = await geolocation.getFreshFix(4_000); } catch { /* keep last */ }

      const r = await api.beginStop(task.id, fix?.lat ?? null, fix?.lng ?? null);
      if (r.too_far && typeof r.distance_m === 'number') {
        setNotice(t('startFar', { m: Math.round(r.distance_m) }));
      }
      navigate(`/stop/${task.id}`);
    } catch (err) {
      // Offline is not a reason to stand outside a hospital doing nothing: the
      // capture screen works without a network and the submission queues.
      reportError('request_timeout', 'route', err);
      navigate(`/stop/${task.id}`);
    } finally {
      setBusyTask(null);
    }
  };

  const doSkip = async (task: Task, reason: SkipReason, note: string | null, scanned = false) => {
    setBusyTask(task.id);
    stopStep(task.id, `skip_pressed:${reason}`);
    /*
     * A FACE SCAN BEFORE THE SKIP THAT CLOSES THE ROUTE (17 Oct).
     *
     * The day's chosen scan stop could be one later closed as "not programmed",
     * after the real stops were delivered, so the route ended with only the
     * pickup scan -- 16 routes, 9 drivers, 1-16 Oct. The office decides: when
     * this skip would close the last open stop of a route with deliveries and
     * no stop scan yet, the scan is taken first. No answer (no signal) means
     * scan as well; it is kept on the phone and sent later.
     */
    if (!scanned) {
      let due = false;
      try { due = (await api.identityDueSkip(task.id)).due; } catch { due = true; }
      if (due) {
        stopStep(task.id, 'skip_needs_scan');
        setBusyTask(null);
        setScanSkip({ task, reason, note });
        return;
      }
    }
    try {
      let fix = geolocation.getLastKnownFix(120_000);
      try { fix = await geolocation.getFreshFix(4_000); } catch { /* optional */ }
      await submitSkip({ taskId: task.id, reason, fix, note });
      stopEnd(task.id, 'skipped');
      setSkipFor(null);
      setSkipReason(null);
      setSkipNote('');
      await load();
    } finally {
      setBusyTask(null);
    }
  };

  if (routes === null) {
    return <main className="screen"><p>{t('loading')}</p></main>;
  }

  if (scanSkip) {
    const s = scanSkip;
    return <IdentityCheck trigger="random_stop" taskId={s.task.id}
                          onFinished={() => { setScanSkip(null); void doSkip(s.task, s.reason, s.note, true); }} />;
  }

  // Read first, enrol second, work third.
  if (showBriefing) {
    return <SecurityBriefing onDone={() => setShowBriefing(false)} />;
  }

  // Before anything else. A route shown first would let it be postponed.
  if (needsFace) {
    return <FaceReference onFinished={() => setNeedsFace(false)} />;
  }

  /*
   * ── THE INSTALL PROMPT IS NO LONGER A WALL ──────────────────────────────
   *
   * It used to render INSTEAD of the route: `if (askInstall) return
   * <InstallGate/>`. On 22 Sep a driver was handed a route at CAPS, opened the
   * app from a browser tab, and was shown a setup screen instead of her stops.
   * She signed in eight times in twenty minutes, we spent half an hour
   * troubleshooting, and the route never ran — five stops, nothing delivered.
   *
   * Installing matters, and the reason has not changed: a tab is the first
   * thing iOS kills, which is what every 'died' report was. But NOTHING may
   * stand between a driver and the work they are stood in front of. It is a
   * banner on the route screen now, and it opens the full instructions only if
   * they ask for them.
   */
  /*
   * FIRST RUN ONLY: the full instructions, once, after the briefing and the
   * face photo — or any time the driver taps "Show me how" on the banner.
   *
   * A new driver on an iPhone will otherwise run in a Safari tab, which iOS
   * shuts down to reclaim memory, and they land back on sign-in with no route.
   * They have to be told at least once, and the only moment they are certain
   * to be looking is while they are setting the phone up.
   *
   * It is NOT a wall: InstallGate carries its own "I cannot do this" link, and
   * it is never shown twice. After this it is the banner and nothing else.
   */
  if (showInstallHelp || (askInstall && !installEverShown())) {
    return <InstallGate onDone={() => { setShowInstallHelp(false); setAskInstall(false); }} />;
  }

  const live = routes.filter((r) => r.tasks.some((x) => !isTerminal(x.status)));
  const done = routes.filter((r) => !r.tasks.some((x) => !isTerminal(x.status)));

  /*
   * Everything done.
   *
   * This used to be a stack of green banners with no heading and no way to
   * refresh, which the owner read as an error screen — fairly, because it looks
   * like one. A driver who has finished should be told so in one sentence, and
   * given the one thing they might still want: to check whether more work has
   * arrived.
   */
  if (routes.length > 0 && live.length === 0) {
    const delivered = routes.reduce(
      (n, r) => n + r.tasks.filter((x) => x.status === 'completed').length, 0);
    return (
      <main className="screen">
        <div className="empty-state">
          <img className="empty-logo" src="/icons/logo.png" alt="JW Express Delivery"
               width={320} height={256} />
          <h1>{t('allDone')}</h1>
          <p className="hint">{t('allDoneBody', { n: delivered })}</p>
          <button className="btn primary big" onClick={() => void load()}>
            {t('checkAgain')}
          </button>
          <div className="empty-actions">
            <Link className="btn secondary" to="/coverage">{t('covOpen')}</Link>
            <Link className="btn secondary" to="/help">{t('needHelp')}</Link>
            <button className="btn link" onClick={async () => {
              await routeTracker.stop();
              await clearToken();
              navigate('/login', { replace: true });
            }}>{t('signOut')}</button>
          </div>
          <p className="build-stamp">
            {String((import.meta as unknown as { env?: Record<string, string> })
              .env?.VITE_APP_VERSION ?? 'dev').split(':').pop()}
          </p>
        </div>
      </main>
    );
  }

  if (routes.length === 0) {
    return (
      <main className="screen">
        <div className="empty-state">
          <img className="empty-logo" src="/icons/logo.png" alt="JW Express Delivery"
               width={320} height={256} />
          <h1>{unreachable ? t('cannotReachTitle') : t('noRoute')}</h1>
          {unreachable && (
            <>
              <div className="banner warn-banner">{t('cannotReachBody')}</div>
              <p className="hint">{t('cannotReachCall')}</p>
            </>
          )}
          <button className="btn primary big" onClick={() => void load()}>
            {t('checkAgain')}
          </button>
          <div className="empty-actions">
            <Link className="btn secondary" to="/coverage">{t('covOpen')}</Link>
            <Link className="btn secondary" to="/help">{t('needHelp')}</Link>
            <button className="btn link" onClick={async () => {
              await routeTracker.stop();
              await clearToken();
              navigate('/login', { replace: true });
            }}>{t('signOut')}</button>
          </div>
          {/*
            * The build, here too.
            *
            * This was the ONE screen without it, and it is the screen a driver
            * stares at when something has gone wrong — so the first question we
            * ask them ("what version does it say?") was unanswerable exactly
            * when it mattered. Two drivers were asked it on 14 Sep and neither
            * could tell us.
            */}
          <p className="build-stamp">
            {String((import.meta as unknown as { env?: Record<string, string> })
              .env?.VITE_APP_VERSION ?? 'dev').split(':').pop()}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="screen home">
      {stale && <div className="banner warn-banner">{t('showingSaved')}</div>}
      {notice && <div className="banner warn-banner">{notice}</div>}
      {/*
        * Shown once, dismissible, and gone for good once the app is installed.
        * It sits above the route because it is the one thing a driver can do
        * that makes every other part of this app more reliable.
        */}
      <InstallPrompt />

      {/*
        * The offer, never the obstacle. One line and a link, above the route
        * but not in place of it. A driver who taps it gets the screenshots;
        * one who ignores it gets straight to work, which is the whole point of
        * the change on 23 Sep.
        */}
      {askInstall && (
        <div className="banner">
          {t('installBanner')}{' '}
          <button className="btn link" onClick={() => setShowInstallHelp(true)}>
            {t('installBannerHow')}
          </button>
        </div>
      )}

      <OfficeMessages onRouteChange={() => { void load(); }} />
      {live.map((route) => {
        const remaining = route.tasks.filter((x) => !isTerminal(x.status));
        const finished = route.tasks.filter((x) => isTerminal(x.status));
        // The one they are on. Everything below it stays closed.
        const next = remaining.find((x) => !sendingIds.has(x.id));

        return (
          <section key={route.id} className="route-block">
            <header className="route-head">
              <h1>{route.name}</h1>
              <p className="route-count">
                {t('stopsLeft', { done: finished.length, total: route.tasks.length })}
              </p>
            </header>

            {remaining.map((task) => {
              // Highlighted, not privileged. The suggested order is a
              // suggestion; only the deadline is a rule, and a driver who
              // reroutes around traffic is doing the right thing.
              const isNext = next !== undefined && task.id === next.id;
              const skipping = skipFor === task.id;

              if (sendingIds.has(task.id)) {
                return (
                  <article key={task.id} className="stop-card queued">
                    <div className="stop-where">
                      <span className="stop-no">{task.sequenceNo}</span>
                      <div>
                        <h2>{task.facility.name}</h2>
                        <p className="stop-sending">{t('stopSending')}</p>
                      </div>
                    </div>
                  </article>
                );
              }

              return (
                <article key={task.id}
                         className={isNext ? 'stop-card next' : 'stop-card'}>
                  <div className="stop-where">
                    <span className="stop-no">{task.sequenceNo}</span>
                    <div>
                      <h2>{task.facility.name}</h2>
                      <p className="muted small">{task.facility.address}</p>
                      {task.cutoffAt && (
                        <p className="muted small">
                          {t('dueBy', {
                            time: new Date(task.cutoffAt)
                              .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
                          })}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* EVERY stop gets buttons. An earlier version put them only
                      on the next one and the owner could not start his second
                      delivery at all. */}
                  {!skipping && (
                    <>
                      {task.facility.deliveryNotes && (
                        <p className="notes">{task.facility.deliveryNotes}</p>
                      )}
                      {task.specialHandling && (
                        <p className="special">{task.specialHandling}</p>
                      )}
                      <div className="stop-actions">
                        <button className="btn primary big"
                                disabled={busyTask === task.id}
                                onClick={() => void start(task)}>
                          {busyTask === task.id ? t('oneMoment') : t('startStop')}
                        </button>
                        <button className="btn secondary big"
                                disabled={busyTask === task.id}
                                onClick={() => setSkipFor(task.id)}>
                          {t('skipStop')}
                        </button>
                      </div>
                      <a className="btn link" href={directionsUrl(task.facility)}
                         target="_blank" rel="noreferrer">{t('navigate')}</a>
                    </>
                  )}

                  {/* Skip asks its question here, in place. It used to be two
                      menus deep. */}
                  {skipping && (
                    <div className="skip-panel">
                      <p className="hint">{t('whySkipping')}</p>
                      {skipReason === null ? (
                        <>
                          {([
                            ['stop_not_programmed_today', t('skipNotAssigned')],
                            ['facility_closed', t('skipClosed')],
                            ['access_denied', t('skipNoAccess')],
                            ['other', t('skipOther')],
                          ] as Array<[SkipReason, string]>).map(([value, label]) => (
                            <button key={value} className="btn secondary big"
                                    disabled={busyTask === task.id}
                                    onClick={() => {
                                      if (value === 'other') setSkipReason('other');
                                      else void doSkip(task, value, null);
                                    }}>
                              {label}
                            </button>
                          ))}
                          <button className="btn link"
                                  onClick={() => setSkipFor(null)}>{t('back')}</button>
                        </>
                      ) : (
                        <>
                          <textarea className="input" rows={3} autoFocus
                                    placeholder={t('skipTypeReason')}
                                    value={skipNote}
                                    onChange={(e) => setSkipNote(e.target.value)} />
                          <button className="btn primary big"
                                  disabled={skipNote.trim().length < 4 || busyTask === task.id}
                                  onClick={() => void doSkip(task, 'other', skipNote.trim())}>
                            {t('skipStop')}
                          </button>
                          <button className="btn link" onClick={() => setSkipReason(null)}>
                            {t('back')}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </article>
              );
            })}

            {/* Done, and out of the way. */}
            {finished.length > 0 && (
              <details className="done-list">
                <summary>{t('finishedStops', { n: finished.length })}</summary>
                <ul className="plain-list">
                  {finished.map((task) => (
                    <li key={task.id} className="done-row">
                      <span className="tick-sm" aria-hidden="true">✓</span>
                      <span>{task.facility.name}</span>
                      <em>{task.status === 'completed' ? t('delivered') : t('skipped')}</em>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        );
      })}

      {/*
        * One quiet line per finished route while others are still running, so a
        * driver can see the first one is closed without it competing with the
        * work in front of them. When EVERYTHING is done the screen above takes
        * over instead — a wall of banners read as an error.
        */}
      {done.length > 0 && (
        <p className="muted small">
          {done.map((r) => r.name).join(' · ')} — {t('finishedStops', { n: done.length })}
        </p>
      )}

      {/*
        * The build, on screen.
        *
        * Without this neither the office nor I could tell which version a phone
        * was actually running, and a week of tests were run against old code
        * that had been "deployed" days earlier. One line, and the question is
        * answerable by looking.
        */}
      <p className="build-stamp">
        {String((import.meta as unknown as { env?: Record<string, string> })
          .env?.VITE_APP_VERSION ?? 'dev').split(':').pop()}
      </p>

      <nav className="route-support">
        <Link className="btn secondary small" to="/help">{t('needHelp')}</Link>
        <Link className="btn secondary small" to="/coverage">{t('covOpen')}</Link>
        <Link className="btn secondary small" to="/guide">{t('guideOpen')}</Link>
      </nav>
    </main>
  );
}
